import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/i18n";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SanctionsSearch from "@/pages/SanctionsSearch";
import OsintPage from "@/pages/OsintPage";
import IcmpPage from "@/pages/IcmpPage";
import PepSearch, { PepPanamaPage } from "@/pages/PepSearch";
import FormNatural from "@/pages/FormNatural";
import FormJuridica from "@/pages/FormJuridica";
import FormDeclaracion from "@/pages/FormDeclaracion";
import FormCertificacion from "@/pages/FormCertificacion";
import FormCarta from "@/pages/FormCarta";
import FormAseguradora from "@/pages/FormAseguradora";
import FormAseguradoraPJ from "@/pages/FormAseguradoraPJ";
import History from "@/pages/History";
import Users from "@/pages/Users";
import RiskClients from "@/pages/RiskClients";
import Employees from "@/pages/Employees";
import Suppliers from "@/pages/Suppliers";
import Ros from "@/pages/Ros";
import Rte from "@/pages/Rte";
import FreezeCases from "@/pages/FreezeCases";
import ManualChanges from "@/pages/ManualChanges";
import CyberAgency from "@/pages/CyberAgency";
import NtasAlerts from "@/pages/NtasAlerts";
import Blacklist from "@/pages/Blacklist";
import Settings from "@/pages/Settings";
import Portal from "@/pages/Portal";
import PublicForm from "@/pages/PublicForm";
import VerifyDoc from "@/pages/VerifyDoc";
import PlatformAdmin from "@/pages/PlatformAdmin";
import PublicRegistry from "@/pages/PublicRegistry";
import TvView from "@/pages/TvView";
import OfacVessel from "@/pages/OfacVessel";
import Landing, { LegalPage } from "@/pages/Landing";

const ROUTE_MOD = {
  "/busqueda": "sanctions", "/fbi": "fbi", "/interpol": "interpol", "/banco-mundial": "wb", "/namus": "namus", "/osint": "osint", "/icmp": "icmp", "/ofac-vessel": "vessels", "/administracion/cyber": "cyber", "/administracion/ntas": "ntas",
  "/busqueda-pep": "pep", "/lista-pep-panama": "pep_panama", "/lista-negra": "blacklist",
  "/formularios/persona-natural": "dd_natural", "/formularios/persona-juridica": "dd_juridica",
  "/formularios/declaracion-fondos": "declaracion", "/formularios/beneficiario-final": "certificacion",
  "/formularios/carta-responsabilidad": "carta", "/formularios/dd-pn-aseguradora": "dd_aseguradora",
  "/formularios/dd-pj-aseguradora": "pj_aseguradora", "/cumplimiento/riesgo": "risk",
  "/cumplimiento/empleados": "employees", "/cumplimiento/proveedores": "suppliers",
  "/cumplimiento/ros": "ros", "/cumplimiento/rte": "rte", "/cumplimiento/congelamiento": "freeze",
  "/cumplimiento/manual": "manual", "/historial": "history",
};
const ALWAYS_ON = ["vessels", "pep_panama"];


function Protected({ children }) {
  const { user } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#16335F]" data-testid="loading-screen">
        <div className="text-white text-sm tracking-widest uppercase animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }
  if (!user) return location.pathname === "/" ? <Landing /> : <Navigate to="/login" replace />;
  const isSuper = user.role === "superadmin" || user.is_super;
  const acting = !!user.is_super;
  const isClient = user.role === "client";
  const clientMods = isClient ? user.modules || [] : [];
  const isPortal = user.role === "provider" || (isClient && clientMods.length === 0);
  if (isSuper && !acting && !["/plataforma", "/plataforma/registros-publicos", "/busqueda-pep"].includes(location.pathname)) return <Navigate to="/plataforma" replace />;
  if (!isSuper && location.pathname === "/plataforma") return <Navigate to="/" replace />;
  if (isPortal && location.pathname !== "/portal" && !ALWAYS_ON.includes(ROUTE_MOD[location.pathname])) return <Navigate to="/portal" replace />;
  if (isClient && clientMods.length > 0) {
    const need = ROUTE_MOD[location.pathname];
    if (need && !ALWAYS_ON.includes(need) && !clientMods.includes(need) && !(need === "pep" && clientMods.includes("pep_panama"))) return <Navigate to="/" replace />;
    if (location.pathname === "/usuarios" || location.pathname === "/configuracion") return <Navigate to="/" replace />;
  }
  const tmods = !isSuper ? user.tenant_modules : null;
  const needMod = ROUTE_MOD[location.pathname];
  if (!isSuper && !isClient && tmods != null && needMod && !ALWAYS_ON.includes(needMod) && !tmods.includes(needMod)) return <Navigate to="/" replace />;
  if (!isPortal && !isSuper && !isClient && location.pathname === "/portal") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/privacidad" element={<LegalPage kind="privacy" />} />
            <Route path="/terminos" element={<LegalPage kind="terms" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registro/:token" element={<PublicForm />} />
            <Route path="/verificar/:number" element={<VerifyDoc />} />
            <Route path="/tv" element={<Protected><TvView /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/plataforma" element={<PlatformAdmin />} />
              <Route path="/plataforma/registros-publicos" element={<PublicRegistry />} />
              <Route path="/portal" element={<Portal />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/busqueda" element={<SanctionsSearch />} />
              <Route path="/fbi" element={<SanctionsSearch fixed="FBI" titleKey="mod.fbi" subtitleKey="mod.fbi_sub" />} />
              <Route path="/interpol" element={<SanctionsSearch fixed="INTERPOL" titleKey="mod.interpol" subtitleKey="mod.interpol_sub" />} />
              <Route path="/banco-mundial" element={<SanctionsSearch fixed="WB" titleKey="mod.wb" subtitleKey="mod.wb_sub" />} />
              <Route path="/namus" element={<SanctionsSearch fixed="NAMUS" titleKey="mod.namus" subtitleKey="mod.namus_sub" />} />
              <Route path="/ofac-vessel" element={<OfacVessel />} />
              <Route path="/osint" element={<OsintPage />} />
              <Route path="/icmp" element={<IcmpPage />} />
              <Route path="/busqueda-pep" element={<PepSearch />} />
              <Route path="/lista-pep-panama" element={<PepPanamaPage />} />
              <Route path="/lista-negra" element={<Blacklist />} />
              <Route path="/formularios/persona-natural" element={<FormNatural />} />
              <Route path="/formularios/persona-juridica" element={<FormJuridica />} />
              <Route path="/formularios/declaracion-fondos" element={<FormDeclaracion />} />
              <Route path="/formularios/beneficiario-final" element={<FormCertificacion />} />
              <Route path="/formularios/carta-responsabilidad" element={<FormCarta />} />
              <Route path="/formularios/dd-pn-aseguradora" element={<FormAseguradora />} />
              <Route path="/formularios/dd-pj-aseguradora" element={<FormAseguradoraPJ />} />
              <Route path="/historial" element={<History />} />
              <Route path="/usuarios" element={<Users />} />
              <Route path="/configuracion" element={<Settings />} />
              <Route path="/cumplimiento/riesgo" element={<RiskClients />} />
              <Route path="/cumplimiento/empleados" element={<Employees />} />
              <Route path="/cumplimiento/proveedores" element={<Suppliers />} />
              <Route path="/cumplimiento/ros" element={<Ros />} />
              <Route path="/cumplimiento/rte" element={<Rte />} />
              <Route path="/cumplimiento/congelamiento" element={<FreezeCases />} />
              <Route path="/cumplimiento/manual" element={<ManualChanges />} />
              <Route path="/administracion/cyber" element={<CyberAgency />} />
              <Route path="/administracion/ntas" element={<NtasAlerts />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
